import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Themes from "@/layouts/partials/themes";
import { trackEvent } from "@/remote/tracking";

export interface ThemeContextProps {
  theme: Theme | CustomTheme;
  setTheme: (theme: Theme | null | CustomTheme) => void;
  switchTheme: () => void;
  resetTheme: () => void;
  boardVisible: boolean;
  showBoard: (visible: boolean) => void;
  backButtonVisible: boolean | string;
  showBackButton: (visible: boolean | string) => void;
  message: MessageCharacters | undefined;
  setMessage: (message: MessageCharacters | undefined) => void;
}

const themeContextDefaults: {
  theme: Theme;
  boardVisible: boolean;
  backButtonVisible: boolean;
  message: MessageCharacters | undefined;
} = {
  theme: "default",
  boardVisible: false,
  backButtonVisible: false,
  message: undefined,
};

const ThemeContext = React.createContext<ThemeContextProps>(
  themeContextDefaults as ThemeContextProps,
);

const themes: Theme[] = ["default", "ice", "dark", "red", "mirage"];

const ThemeProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [themeState, setThemeState] = useState<Theme>(
    themeContextDefaults.theme,
  );

  const [customThemeState, setCustomThemeState] = useState<CustomTheme | null>(
    null,
  );

  const theme = useMemo<Theme | CustomTheme>(
    () => customThemeState ?? themeState,
    [themeState, customThemeState],
  );

  const [boardVisible, showBoard] = useState<boolean>(
    themeContextDefaults.boardVisible,
  );

  const [message, setMessage] = useState<MessageCharacters | undefined>(
    themeContextDefaults.message,
  );

  const [backButtonVisible, showBackButton] = useState<boolean | string>(
    themeContextDefaults.backButtonVisible,
  );

  const resetTheme = useCallback(
    () => setCustomThemeState(null),
    [setCustomThemeState],
  );

  const clearTheme = useCallback(() => {
    setThemeState(themeContextDefaults.theme);

    window?.localStorage?.removeItem("theme");
  }, [setThemeState]);

  const setTheme = useCallback(
    (theme: Theme | CustomTheme | null) => {
      if (!theme || theme === themeContextDefaults.theme) {
        clearTheme();
        return;
      }

      if (typeof theme === "object") {
        setCustomThemeState(theme);
        return;
      }

      setThemeState(theme);

      if (typeof theme === "string") {
        window?.localStorage?.setItem("theme", JSON.stringify(theme));
      }
    },
    [setThemeState, setCustomThemeState, clearTheme],
  );

  const switchTheme = useCallback(() => {
    setTheme(themes[themes.findIndex((t) => t === theme) + 1] ?? null);
    trackEvent("Theme geändert");
  }, [setTheme, theme]);

  useEffect(() => {
    const storedTheme = window?.localStorage?.getItem("theme");

    if (storedTheme) {
      setTheme(JSON.parse(storedTheme) as Theme);
    }
  }, [setTheme]);

  const value = useMemo(
    () => ({
      theme,
      switchTheme,
      setTheme,
      resetTheme,
      boardVisible,
      showBoard,
      backButtonVisible,
      showBackButton,
      message,
      setMessage,
    }),
    [
      theme,
      setTheme,
      resetTheme,
      switchTheme,
      boardVisible,
      showBoard,
      backButtonVisible,
      showBackButton,
      message,
      setMessage,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>
      <Themes />
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;

export { ThemeProvider };

export const useTheme = () => useContext(ThemeContext);
